import { useReducer } from 'react';
import { searchPageApi } from '../../../api/searchPageApi';
import type {
  IDFP,
  IGetAdsRequestFilter,
  IGetAdsRequestRange,
  ISearchPageAds,
  ISearchPagePaging,
  ISearchPageSeoData,
  SearchPageBreadcrumbs,
} from '../../../api/types/searchPageApiTypes';
import { GTMEvent } from '../../../services/gtmService';
import {
  mapFiltersRequest,
  mapGeoFilterRequest,
  mapLegacyAreaFilterValues,
  mapRangesRequest,
} from '../../../components/DealerPage/mappers/filters';
import type { GeoFilter } from '../../../types';
import { type MakeModelType } from '../../../components/DealerPage/hooks/useDealerShowroomAds';

export enum ACTION_TYPE {
  SET_STATE = 'SET_STATE',
  SET_LOADING = 'SET_LOADING',
  SET_ADS = 'SET_ADS',
  SET_ERROR = 'SET_ERROR',
  RESET = 'RESET',
}

export type AdsType = ISearchPageAds[] | [];
export type SeoDataType = ISearchPageSeoData;
export type ErrorType = Error | null;

type SetLoadingType = {
  type: ACTION_TYPE.SET_LOADING;
  payload: boolean;
};

type SetAdsType = {
  type: ACTION_TYPE.SET_ADS;
  payload: {
    ads?: AdsType;
    spotlights?: AdsType;
    seoData?: SeoDataType;
    dfp: IDFP;
    gtm: GTMEvent;
  };
};

type SetErrorType = {
  type: ACTION_TYPE.SET_ERROR;
  payload: ErrorType;
};

type StateType = {
  isLoading: boolean;
  error: ErrorType;
  ads?: AdsType;
  spotlights?: AdsType;
  seoData?: SeoDataType;
  dfp: IDFP;
  gtm: GTMEvent;
};

type ActionType = SetLoadingType | SetAdsType | SetErrorType;

export const useAdsReducer = (state: StateType, action: ActionType) => {
  const { type, payload } = action;
  switch (type) {
    case ACTION_TYPE.SET_LOADING: {
      return {
        ...state,
        isLoading: payload as boolean,
      };
    }
    case ACTION_TYPE.SET_ADS: {
      const { ads, spotlights, seoData, dfp, gtm } = payload as StateType;
      return {
        isLoading: false,
        ads,
        spotlights,
        error: null,
        seoData,
        dfp,
        gtm,
      };
    }
    case ACTION_TYPE.SET_ERROR: {
      return {
        ...state,
        isLoading: false,
        error: payload as ErrorType,
      };
    }
    default:
      return state;
  }
};

export const mapMakeModelFilters = (makeModelFilters: MakeModelType) => {
  let response: { make: string; model: string; trim: string }[] = [];
  makeModelFilters.forEach((item) => {
    if (!item.makeValue || item.makeValue === 'All Makes') return;
    if (item.modelValue.length > 0) {
      item.modelValue.forEach((model) => {
        response.push({
          make: item.makeValue,
          model: model,
          trim: '',
        });
      });
    } else {
      response.push({
        make: item.makeValue,
        model: '',
        trim: '',
      });
    }
  });
  return response;
};

export type UseAdsType = StateType & {
  setLoading: (loading: boolean) => void;
  setAds: (
    ads: AdsType,
    spotlights: AdsType | undefined,
    seoData: SeoDataType | undefined,
    dfp: IDFP,
    gtm: GTMEvent,
  ) => void;
  setError: (error: ErrorType) => void;
  fetchAds: (
    filters: IGetAdsRequestFilter[],
    ranges: IGetAdsRequestRange[],
    makeModel: MakeModelType,
    from: number,
    pageSize: number,
    section: string,
    search: string,
    sort: string,
    userId: string | string[] | undefined,
    onStart: () => void,
    onSuccess: (
      breadcrumbs: SearchPageBreadcrumbs[],
      recommendations?: ISearchPageAds[],
    ) => void,
    geoFilter?: GeoFilter,
  ) => void;
  combineAds: (ads?: AdsType, spotlightAds?: AdsType) => ISearchPageAds[];
};

const useAds = (
  defaultAds: ISearchPageAds[] = [],
  defaultSpotlights: ISearchPageAds[] = [],
  defaultSeoData: ISearchPageSeoData | undefined,
  defaultDFP: IDFP,
  defaultGTM: GTMEvent,
  setPaging: (paging: ISearchPagePaging) => void,
): UseAdsType => {
  const [state, dispatch] = useReducer(useAdsReducer, {
    isLoading: false,
    ads: defaultAds,
    spotlights: defaultSpotlights,
    error: null,
    seoData: defaultSeoData,
    dfp: defaultDFP,
    gtm: defaultGTM,
  });

  const setLoading = (loading: boolean) =>
    dispatch({
      type: ACTION_TYPE.SET_LOADING,
      payload: loading,
    });

  const setAds = (
    ads: AdsType | undefined,
    spotlights: AdsType | undefined,
    seoData: SeoDataType | undefined,
    dfp: IDFP,
    gtm: GTMEvent,
  ) =>
    dispatch({
      type: ACTION_TYPE.SET_ADS,
      payload: {
        ads: ads as AdsType,
        spotlights: spotlights as AdsType,
        seoData: seoData as SeoDataType,
        dfp,
        gtm,
      },
    });

  const setError = (error: ErrorType) =>
    dispatch({
      type: ACTION_TYPE.SET_ERROR,
      payload: error,
    });

  const fetchAds = async (
    filters: IGetAdsRequestFilter[],
    ranges: IGetAdsRequestRange[],
    makeModel: MakeModelType,
    from: number,
    pageSize: number,
    section: string,
    search: string,
    sort: string,
    userId: string | string[] | undefined,
    onStart: () => void,
    onSuccess: (
      breadcrumbs: SearchPageBreadcrumbs[],
      recommendations?: ISearchPageAds[],
    ) => void,
    geoFilter?: GeoFilter,
  ) => {
    setLoading(true);
    onStart();
    const editedFilters = [...filters];

    if (userId) {
      Array.isArray(userId)
        ? editedFilters.push({
            name: 'userId',
            values: userId,
          })
        : editedFilters.push({
            name: 'userId',
            values: [userId],
          });
    }

    const mappedFilterValues = mapLegacyAreaFilterValues(
      editedFilters,
      geoFilter?.county,
      geoFilter?.countyTown,
    );

    try {
      const response = await searchPageApi.getAds(
        {
          geoFilter: mapGeoFilterRequest(geoFilter),
          filters: mapFiltersRequest(mappedFilterValues),
          ...(ranges.length && { ranges: mapRangesRequest(ranges) }),
          ...(makeModel.length && {
            makeModelFilters: mapMakeModelFilters(makeModel),
          }),
          paging: {
            pageSize,
            from,
          },
          sections: [section],
          terms: search,
          sort,
        },
        { accept: 'application/json' },
        '12000',
      );
      const {
        ads,
        spotlights,
        paging,
        seoData,
        dataTargetingProps: { dfp, gtm },
        breadcrumbs,
        recommendations,
      } = response.data;
      setAds(ads, spotlights, seoData, dfp, gtm);
      setPaging(paging);
      onSuccess(breadcrumbs, recommendations);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching ads'),
      );
    }
  };

  const mapSpotlightAds: (s?: AdsType) => ISearchPageAds[] = (spotlights) => {
    const mappedSpotlights: ISearchPageAds[] = [];
    spotlights &&
      spotlights.forEach((spotlight: ISearchPageAds) => {
        mappedSpotlights.push({
          ...spotlight,
          highlight: 'ORANGE',
          highlightText: 'SPOTLIGHT',
        });
      });
    return mappedSpotlights;
  };

  const combineAds = (ads?: AdsType, spotlightAds?: AdsType) => {
    let combinedAds = [
      ...(mapSpotlightAds(spotlightAds) || []),
      ...(ads || []),
    ];
    return combinedAds;
  };

  return {
    ...state,
    fetchAds,
    setLoading,
    setAds,
    setError,
    combineAds,
  };
};

export { useAds };
